import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Table,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { ScheduleDetails } from "../../serviceoperation/entity/ScheduleOperationEntity";
import { PlusOutlined } from "@ant-design/icons";
import { ScheduleOperation } from "../../serviceoperation/ScheduleOperation";
import { CompanyContext } from "../../contexts/company-context";
import { AmPMFormat, ConvertTo12hr } from "../../Util/dateutil";

interface Item {
  key: number;
  activityType: string;
  activityDateTime: any;
  newRow: boolean;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text" | "date" | "dropdown";
  record: Item;
  index: number;
  children: React.ReactNode;
}

interface ModifiyFormProps {
  scheduleDetail: ScheduleDetails;
  open: boolean;
  onCreate: (data: Item[]) => void;
  onCancel: () => void;
}

const ScheduleActivityModify: React.FC<ModifiyFormProps> = ({
  scheduleDetail,
  open,
  onCreate,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { Text } = Typography;

  const { selectedCompany } = useContext(CompanyContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<Item[]>([]);
  const [disableAdd, setDisableAdd] = useState<boolean>(false);
  const [disableDone, setDisableDone] = useState<boolean>(false);

  const [editingKey, setEditingKey] = useState<number>(-1);
  const [activityTypes, setActivityTypes] = useState([
    {
      value: "CLOCK_IN",
      label: "CLOCK_IN",
    },
    {
      value: "BREAK_START",
      label: "BREAK_START",
    },
    {
      value: "BREAK_END",
      label: "BREAK_END",
    },
    {
      value: "CLOCK_OUT",
      label: "CLOCK_OUT",
    },
  ]);
  const isEditing = (record: Item) => record.key === editingKey;

  // Compare datetime values and throw validation error if necessary
  const validateDatetime =
    (index: number) => (rule: any, value: Date | null) => {
      if (data && value) {
        const dataIndex = data.findIndex((x) => x.key === index);

        if (dataIndex !== -1) {
          const upperBoundData = data[dataIndex - 1];
          const lowerBoundData = data[dataIndex + 1];

          if (
            (!upperBoundData || upperBoundData.activityDateTime < value) &&
            (!lowerBoundData || lowerBoundData.activityDateTime > value)
          ) {
            return Promise.resolve();
          }
        }
      }
      return Promise.reject(
        "Ensure that the date and time are greater than the previous row and less than the subsequent row."
      );
    };

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === "number" ? (
        <InputNumber />
      ) : inputType === "date" ? (
        <DatePicker
          //defaultValue={record.activityDateTime}
          value={record.activityDateTime}
          format="YYYY-MM-DD h:mm a"
          showTime
          size="small"
          style={{ width: "100%" }}
          key={record.key}
        />
      ) : inputType === "dropdown" ? (
        <Select
          disabled={!record.newRow}
          placeholder="Select Activity"
          key={record.key}
          value={record.activityType}
          options={activityTypes}
          // This is really a hack but it works
          onChange={(value: string) => {
            if (value === "CLOCK_OUT") {
              const newData = {
                key: record.key,
                // If There is no activity then
                // select the activityType to be CLOCK_IN
                //activityTypes
                activityType: value,
                // Set this to current Schedule DateTime...
                activityDateTime: dayjs(scheduleDetail.workDate)
                  .add(scheduleDetail.endWorkHour, "hour")
                  .add(scheduleDetail.endWorkMinute, "minute"), //scheduleDetail.workDate ,
                newRow: true,
              };
              edit(newData);
            }
          }}
        />
      ) : (
        <Input />
      );
    return (
      <td {...restProps}>
        {editing && inputType !== "date" ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : editing && inputType === "date" ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                validator: validateDatetime(record.key),
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  useEffect(() => {
    setData(
      scheduleDetail?.shiftActivity.map((item) => ({
        key: item.scheduleActivityID,
        activityType: item.activityType,
        activityDateTime: dayjs(item.activityDateTime + "Z"),
        newRow: false,
      }))
    );
  }, []);

  useEffect(() => {
    if (data) {
      let lastItem = data[data.length - 1];

      if (lastItem?.newRow === true) {
        lastItem = data[data.length - 2];
      }

      const activityTypeLookup: Record<
        string,
        { value: string; label: string }[]
      > = {
        CLOCK_IN: [
          { value: "BREAK_START", label: "BREAK_START" },
          { value: "BREAK_END", label: "BREAK_END" },
          { value: "CLOCK_OUT", label: "CLOCK_OUT" },
        ],
        CLOCK_OUT: [],
        BREAK_START: [{ value: "BREAK_END", label: "BREAK_END" }],
        BREAK_END: [
          { value: "BREAK_START", label: "BREAK_START" },
          { value: "CLOCK_OUT", label: "CLOCK_OUT" },
        ],
      };

      const lastActivityType = lastItem?.activityType || "default";
      const activityTypes = activityTypeLookup[lastActivityType] || [
        { value: "CLOCK_IN", label: "CLOCK_IN" },
      ];
      const disableAdd = lastActivityType === "CLOCK_OUT";

      setActivityTypes(() => activityTypes);
      setDisableAdd(() => disableAdd);
    }
  }, [data]);

  const edit = (record: Partial<Item> & { key: React.Key }) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
    setDisableAdd(true);
    setDisableDone(true);
  };

  const cancel = (value?: Item) => {
    setEditingKey(-1);
    if (value?.newRow) {
      const updatedData = [...data].filter((item) => item.key !== value.key);
      setData(updatedData);
    }
    setDisableAdd(false);
    setDisableDone(false);
  };

  const save = async (key: React.Key) => {
    try {
      setLoading(true);
      const row = (await form.validateFields()) as Item;

      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);
      const item = newData[index];

      const scheduleOps = new ScheduleOperation();
      await scheduleOps
        .patchScheduleActivityAsync(
          selectedCompany?.corporationId as number,
          scheduleDetail.scheduleID,
          [
            {
              path: "/",
              op: item.newRow === false ? "replace" : "add",
              value: {
                ActivityId: key as number,
                ActivityType: row.activityType,
                DateTimeUTC: new Date(row.activityDateTime).toISOString(),
              },
            },
          ]
        )
        .then((x) => {
          if (x.statusCode === 200) {
            message.success("Successfully processed your request!");
            // On the schedule Cancel, navigate to Schedule Page.

            if (index > -1) {
              //const item = newData[index];
              // Set Row to not new data...
              item.newRow = false;
              newData.splice(index, 1, {
                ...item,
                ...row,
              });
              setData(newData);
              setEditingKey(-1);
            } else {
              newData.push(row);
              setData(newData);
              setEditingKey(-1);
            }
            setDisableAdd(false);
            setDisableDone(false);
          } else {
            message.error("There was an error trying to process your request!");
          }
          setLoading(false);
        })
        .catch((x) => {
          setLoading(false);
          message.error("There was an error trying to process your request!");
        });
    } catch (errInfo) {
      setLoading(false);
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleAdd = () => {
    let selectedDate = dayjs(scheduleDetail.workDate)
      .add(scheduleDetail.startWorkHour, "hour")
      .add(scheduleDetail.startWorkMinute, "minute");
    const selectedActivity =
      activityTypes.length === 1 ? activityTypes[0].value : "";

    if (selectedActivity === "CLOCK_OUT") {
      selectedDate = dayjs(scheduleDetail.workDate)
        .add(scheduleDetail.endWorkHour, "hour")
        .add(scheduleDetail.endWorkMinute, "minute");
    }

    const newData = {
      key: Date.now(),
      // If There is no activity then
      // select the activityType to be CLOCK_IN
      //activityTypes
      activityType: selectedActivity,
      // Set this to current Schedule DateTime...
      activityDateTime: selectedDate, //scheduleDetail.workDate ,
      newRow: true,
    };
    const updatedData = [...data, newData];
    setData(updatedData);
    edit(newData);
  };

  const columns = [
    {
      title: "Activity ID",
      dataIndex: "key",
      width: "12%",
      editable: false,
    },
    {
      title: "Activity Type",
      dataIndex: "activityType",
      width: "20%",
      editable: true,
    },
    {
      title: "Date Time",
      dataIndex: "activityDateTime",
      width: "40%",
      editable: true,
      render: (_: any, record: Item) =>
        new Date(record.activityDateTime).toLocaleTimeString([], {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      title: "Action",
      dataIndex: "operation",
      render: (_: any, record: Item) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              Save
            </Typography.Link>
            <Popconfirm title="Are you sure?" onConfirm={() => cancel(record)}>
              <a>Cancel</a>
            </Popconfirm>
          </span>
        ) : (
          <Typography.Link
            disabled={editingKey !== -1}
            onClick={() => edit(record)}
          >
            Edit
          </Typography.Link>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType:
          col.dataIndex === "activityDateTime"
            ? "date"
            : col.dataIndex === "activityType"
            ? "dropdown"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Modal
      open={open}
      title={`${scheduleDetail?.employee.firstName} - Schedule ${dayjs(
        scheduleDetail?.workDate
      ).format("dddd, MMMM Do")}`}
      okText="Done"
      cancelText="Cancel"
      onCancel={onCancel}
      footer={[
        <Button
          key="okay"
          disabled={disableDone}
          type="primary"
          loading={loading}
          onClick={() => {
            onCreate(data);
          }}
        >
          Done
        </Button>,
      ]}
      width={1000}
    >
      <Text style={{ fontSize: "16px" }}>
        Schedule Time : {ConvertTo12hr(scheduleDetail?.startWorkHour ?? 0)}:
        {scheduleDetail?.startWorkMinute === 0
          ? "00"
          : scheduleDetail?.startWorkMinute}{" "}
        {AmPMFormat(scheduleDetail?.startWorkHour ?? 0)}
        {" - "}
        {ConvertTo12hr(scheduleDetail?.endWorkHour ?? 0)}:
        {scheduleDetail?.endWorkMinute === 0
          ? "00"
          : scheduleDetail?.endWorkMinute}{" "}
        {AmPMFormat(scheduleDetail?.endWorkHour ?? 0)}
      </Text>

      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            onChange: () => cancel(),
          }}
        />
        <Button
          type="primary"
          onClick={handleAdd}
          disabled={disableAdd}
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <PlusOutlined /> Add Activity
        </Button>
      </Form>
    </Modal>
  );
};

export default ScheduleActivityModify;
